import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { Button, ErrorState, Skeleton } from '@/design/components';
import { useSettings } from '@/access/SettingsContext';
import { usePerm } from '@/access/usePerm';
import { formatDate, weekdayName } from '@/lib/dates';
import { closureFor, hoursLabel, useBoard } from './api';

/**
 * גיליון הסידור להדפסה או לשמירה כ PDF (PRD 5.4.8).
 *
 * למה הדפסת דפדפן ולא ספריית PDF: עברית ו RTL נשברים כמעט בכל ספריית
 * PDF, ודורשים הטמעת גופן ידנית. הדפדפן כבר מרנדר את העמוד הזה נכון,
 * ו"שמירה כ PDF" מובנית בכל דפדפן בדסקטופ ובטלפון. אפס תלויות.
 *
 * העיצוב כאן הוא שחור על לבן בכוונה: הדף נתלה על לוח בסניף, ודיו
 * צבעונית מתבזבזת.
 */
export function PrintSchedulePage() {
  const { periodId = '' } = useParams();
  const canManage = usePerm('schedule.manage');
  const board = useBoard(periodId);
  const { brand } = useSettings();

  // כותרת הדפדפן הופכת לשם קובץ ה PDF כברירת מחדל
  useEffect(() => {
    if (!board.data) return;
    const previous = document.title;
    document.title = `סידור עבודה ${formatDate(board.data.period.start_date)}`;
    return () => {
      document.title = previous;
    };
  }, [board.data]);

  if (!canManage) return <Navigate to="/admin" replace />;
  if (board.isPending) return <Skeleton className="m-8 h-64" />;
  if (board.isError) return <ErrorState onRetry={() => void board.refetch()} />;
  if (!board.data) return <Navigate to="/admin/schedule" replace />;

  const data = board.data;
  const branchName = data.branch_name ?? brand.name;

  const nameOf = (employeeId: string) => data.staff.find((s) => s.id === employeeId)?.name ?? '';

  return (
    <div className="min-h-dvh bg-white p-6 text-black print:p-0">
      <div className="mx-auto max-w-5xl">
        {/* סרגל הפעולה נעלם בהדפסה */}
        <div className="mb-6 flex flex-wrap items-center gap-2 print:hidden">
          <Button variant="primary" onClick={() => window.print()}>
            <Printer size={20} strokeWidth={1.75} aria-hidden="true" />
            הדפסה או שמירה כ PDF
          </Button>
          <p className="m-0 text-body-sm text-muted">
            בחלון ההדפסה אפשר לבחור "שמירה כ PDF" כדי לשלוח בוואטסאפ.
          </p>
        </div>

        <header className="mb-4 border-b-2 border-black pb-3">
          <h1 className="m-0 font-display text-display-l font-bold">סידור עבודה</h1>
          <p className="m-0 text-title">
            {formatDate(data.period.start_date)} — {formatDate(data.period.end_date)}
            {branchName && <span> · {branchName}</span>}
          </p>
        </header>

        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="w-28 border border-black p-2 text-start text-body-sm">משמרת</th>
              {data.days.map((date) => (
                <th key={date} className="border border-black p-2 text-start">
                  <span className="block text-body-sm font-bold">{weekdayName(date)}</span>
                  <span className="block text-label font-normal tabular">{formatDate(date)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.shift_types.map((shift) => (
              <tr key={shift.id}>
                <th className="border border-black p-2 text-start align-top">
                  <span className="block text-body-sm font-bold">{shift.name}</span>
                  <span className="block text-label font-normal tabular" dir="ltr">
                    {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                  </span>
                </th>

                {data.days.map((date) => {
                  const closure = closureFor(data, date, shift.id);
                  const assigned = data.assignments.filter(
                    (a) => a.date === date && a.shift_type_id === shift.id,
                  );

                  return (
                    <td key={date} className="border border-black p-2 align-top">
                      {closure?.is_closed ? (
                        <span className="text-label">סגור — {closure.label}</span>
                      ) : assigned.length === 0 ? (
                        <span className="text-label">—</span>
                      ) : (
                        <ul className="m-0 grid list-none gap-0.5 p-0">
                          {assigned.map((a) => (
                            <li key={a.id} className="text-body-sm">
                              {nameOf(a.employee_id)}
                              {hoursLabel(a) && (
                                <span className="text-label"> {hoursLabel(a)}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <footer className="mt-4 text-label">הופק ב {formatDate(new Date())}</footer>
      </div>
    </div>
  );
}
